import { useState, useContext, useEffect, useMemo } from 'react'
import { PassContext } from '../pass-context'
import _ from "lodash"


import { TextField, Typography, Tooltip, Button, FormControlLabel, FormLabel, RadioGroup, Radio } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'

import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'

import { AppContext } from '/providers/app-context'
import dataSelector from './data-selector'
import PopupShell from '../popup-shell'
import { validateRequiredVariableValue } from '/utils/variable-required'

const defaultBarcode = {
  type: "{{barcode.format}}",
  value: "{{barcode.payload}}",
  alternateText: "{{barcode.text}}"
}

const PopupDialogBarcode = ({ open, close }) => {

  const { client } = useContext(AppContext)
  const { classData, setClassData, objectData, variables, setObjectData, setVariables, setGoogleBoth, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)

  const [newClassData, setNewClassData] = useState({})
  const [newVariables, setNewVariables] = useState({})
  const [barcode, setBarcode] = useState(null)
  const [readonly,setReadonly] = useState(false)
  const isAdminUser = Number(client?.client_id) === 0
  const catalogMap = _.keyBy(variablesCatalog, "var_name")
  const workingBarcode = barcode ?? defaultBarcode

  const barcodeFormat = useMemo(() => {
    return dataSelector({ objectData: workingBarcode, variables: newVariables, path: "type" }) ?? "NONE"
  }, [newVariables, workingBarcode])

  const payloadRequiredValidation = useMemo(() => validateRequiredVariableValue({
    value: _.get(newVariables, "barcode.payload"),
    catalogEntry: catalogMap["barcode.payload"],
    label: "Barcode payload",
    isManagedPassTemplate
  }), [catalogMap, isManagedPassTemplate, newVariables])
  
  const textRequiredValidation = useMemo(() => validateRequiredVariableValue({
    value: _.get(newVariables, "barcode.text"),
    catalogEntry: catalogMap["barcode.text"],
    label: "Barcode text",
    isManagedPassTemplate
  }), [catalogMap, isManagedPassTemplate, newVariables])

  // Runs on "open" state change or if object / variables change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      if (open) {
        setNewVariables(_.cloneDeep(variables))
        setNewClassData(_.cloneDeep(classData))
        setReadonly(false)
        setBarcode(null)
        
        if (objectData.barcode) {
          const initialBarcode = _.get(objectData, "barcode")
          setBarcode(initialBarcode)
          if (initialBarcode.readonly) setReadonly(true)
        }
      }
    }
    catch (err) {
      throw err
    }
  }, [open, classData, objectData, variables])


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const setSecurityAnimation = enable => {
    setNewClassData(prev => {
      let temp = {...prev}
      if (enable) {
        _.set(temp,"securityAnimation.animationType","FOIL_SHIMMER")
      } else {
        _.unset(temp,"securityAnimation")
      }
      return temp
    })
  }

  const instanceFields = {
    value : "barcode.payload",
    alternateText : "barcode.text",
    type : "barcode.format"

  }

  const updateBarcode = (field, value) => {
    if (fieldReadonly(field)) return

    const currentBarcode = barcode ?? defaultBarcode
    const currentFieldValue = _.get(currentBarcode, field)
    const isVariableBackedField = typeof currentFieldValue === "string" && /^{{.+}}$/.test(currentFieldValue)

    setBarcode(prev => {
      const nextBarcode = _.cloneDeep(prev ?? defaultBarcode)
      if (!isVariableBackedField) _.set(nextBarcode, field, value)
      return nextBarcode
    })

    if (isVariableBackedField) {
      setNewVariables((prev) => { 
        let t = {...prev}
        _.set(t,instanceFields[field],value)
        return t
      })
    }
  }

  const fieldReadonly = field => {
    const catalogEntry = catalogMap[instanceFields[field]]
    return catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const handleChangeReadonly = enabled => {
    setReadonly(enabled)
    setBarcode(prev => {
      const nextBarcode = { ...(prev ?? defaultBarcode) }
      if (enabled) nextBarcode.readonly = true
      else delete nextBarcode.readonly
      return nextBarcode
    })
  }

  const saveFormData = () => {
    const nextBarcode = barcode ? { ...barcode } : barcode

    if (!_.isEqual(newClassData, classData) && !_.isEqual(objectData.barcode, nextBarcode)) {
      setGoogleBoth({
        googleClass: newClassData,
        googleObject: { ...objectData, barcode: nextBarcode }
      })
    } else {
      if (!_.isEqual(newClassData, classData)) setClassData(newClassData)

      if (!_.isEqual(objectData.barcode, nextBarcode)) setObjectData(prev => {
        let temp = {...prev}
        _.set(temp,"barcode",nextBarcode)
        return temp
      })
    }

    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    close()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const barcodeReadonly = readonly || fieldReadonly("value") || fieldReadonly("alternateText") || fieldReadonly("type")

  try {
    return (
      <PopupShell open={open} onClose={handleClose}>
        <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

          <div className="flex flex-row justify-between mt-5 w-full">
            <Typography color="secondary" variant="h6">Barcode</Typography>
            <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
          </div>


          {isAdminUser && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
              <Typography>Barcode editable</Typography>
              <Switch checked={readonly} onChange={e => handleChangeReadonly(e.target.checked)} />
              <Typography>Barcode readonly</Typography>
            </Stack>
          )}
          
          {barcodeReadonly && 
            <Typography  variant="h5">
              <p>This barcode is read-only.</p> 
              <p>The barcode type and content cannot be modified in the editor.</p>
            </Typography>
          }

          <RadioGroup
            value={barcodeFormat}
            onChange={e => updateBarcode("type",e.target.value)}
          >
            <FormControlLabel value="NONE" control={<Radio disabled={barcodeReadonly} />} label="No barcode" />
            <FormControlLabel value="AZTEC" control={<Radio disabled={barcodeReadonly}/>} label="Aztec" />
            <FormControlLabel value="QR_CODE" control={<Radio disabled={barcodeReadonly} />} label="QR Code" />
            <FormControlLabel value="PDF_417" control={<Radio disabled={barcodeReadonly} />} label="PDF 417" />
            <FormControlLabel value="CODE_128" control={<Radio disabled={barcodeReadonly} />} label="Code 128" />
          </RadioGroup>

          <div className='flex flex-col gap-2'>
            <FormLabel id="barcode_type_label">Barcode Payload</FormLabel>
            <DataSourceData barcode={workingBarcode} newVariables={newVariables} field="value" updateBarcode={updateBarcode} readonly={barcodeReadonly || fieldReadonly("value")} requiredValidation={payloadRequiredValidation}/>
          </div>

          <div className='flex flex-col gap-2'>
            <FormLabel id="barcode_type_label">Barcode Text</FormLabel>
            <DataSourceData barcode={workingBarcode} newVariables={newVariables} field="alternateText" updateBarcode={updateBarcode} readonly={barcodeReadonly || fieldReadonly("alternateText")} requiredValidation={textRequiredValidation}/>
          </div>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography>Security animation : Off</Typography>
            <Switch checked={!!newClassData.securityAnimation} onChange={e => setSecurityAnimation(e.target.checked)}/>
            <Typography>On</Typography>
          </Stack>

          <div className='fixed bottom-5 w-[460px]'>
            <hr className='mb-5' />
            <div className="flex flex-row w-full justify-end">
              <Button variant="contained" onClick={saveFormData} disabled={!payloadRequiredValidation.valid || !textRequiredValidation.valid}>Save</Button>
            </div>
          </div>
        </div>
      </PopupShell >
    )
  }
  catch (err) {
    throw err
  }
}


const DataSourceData = ({ barcode, newVariables, field, updateBarcode,readonly, requiredValidation }) => {

  const resolvedInstanceValue = dataSelector({ objectData: barcode, variables: newVariables, path: field })

  return (
    <>
      <TextField
        value={resolvedInstanceValue ?? ""}
        slotProps={{ inputLabel: { shrink: true } }}
        type="text"
        onChange={e => { updateBarcode(field, e.target.value) }}
        fullWidth
        multiline={field === "value"}
        maxRows={10}    
        disabled={readonly}
        error={!requiredValidation.valid}
        helperText={requiredValidation.helperText || undefined}
      />
    </>
  )
}

export default PopupDialogBarcode
